import { useMemo } from "react"
import { useMacros, useMacrosOnce } from "./use-macros"
import { useTokenLabels } from "./use-token-labels"

/**
 * Combines the standard macro map with resolved component token labels.
 * Returns a single lookup that works for both regular macros and component
 * tokens like "cmp:42::actionsHtml".
 *
 * @param {Object} options
 * @param {string[]} options.tokenIds - All token IDs found in the current schedule
 * @param {boolean} options.retrieveDeleted - Whether to include deleted macros
 */
export function useEnrichedMacroMap({ tokenIds = [], retrieveDeleted = false } = {}) {
    const { macros, macroMap, loading: macrosLoading, error: macrosError } = useMacros({ retrieveDeleted })
    const {
        tokenLabelMap,
        hasComponentTokens,
        loading: tokenLabelsLoading,
        error: tokenLabelsError,
    } = useTokenLabels(tokenIds)

    const enrichedMap = useMemo(buildEnrichedMap, [macroMap, tokenLabelMap, hasComponentTokens])
    const loading = macrosLoading || tokenLabelsLoading

    return { macros, macroMap: enrichedMap, loading, error: macrosError || tokenLabelsError, hasComponentTokens }

    function buildEnrichedMap() {
        if (!hasComponentTokens) return macroMap
        return { ...macroMap, ...tokenLabelMap }
    }
}

/** Like useEnrichedMacroMap but loads macros once — avoids invalidation-driven editor redraws. */
export function useEnrichedMacroMapOnce({ tokenIds = [], retrieveDeleted = false } = {}) {
    const { macros, macroMap, loading: macrosLoading, error: macrosError } = useMacrosOnce({ retrieveDeleted })
    const {
        tokenLabelMap,
        hasComponentTokens,
        loading: tokenLabelsLoading,
        error: tokenLabelsError,
    } = useTokenLabels(tokenIds)

    const enrichedMap = useMemo(buildEnrichedMap, [macroMap, tokenLabelMap, hasComponentTokens])
    const loading = macrosLoading || tokenLabelsLoading

    return { macros, macroMap: enrichedMap, loading, error: macrosError || tokenLabelsError, hasComponentTokens }

    function buildEnrichedMap() {
        if (!hasComponentTokens) return macroMap
        return { ...macroMap, ...tokenLabelMap }
    }
}
